import { createSlice, createAsyncThunk, createEntityAdapter } from "@reduxjs/toolkit";
import { request } from "@/utils/api";

// Create entity adapter
const pickupAddressAdapter = createEntityAdapter({
    selectId: (address) => address._id,
    sortComparer: (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
});

// Async Thunks
export const fetchPickupAddresses = createAsyncThunk(
    "pickupAddress/fetch",
    async(_, { rejectWithValue }) => {
        try {
            const response = await request({
                endpoint: "/user/getPickupAddress",
                method: "GET",
            });

            if (!response.data || !Array.isArray(response.data.address)) {
                return rejectWithValue(response.error.message || "Failed to fetch pickup addresses.");
            }

            return response.data.address;
        } catch ({ error }) {
            return rejectWithValue(error.message || "An unexpected error occurred while fetching pickup addresses.");
        }
    }
);

export const savePickupAddress = createAsyncThunk(
    "pickupAddress/save",
    async(data, { rejectWithValue }) => {
        try {
            const response = await request({
                endpoint: "/user/savePickupAddress",
                method: "POST",
                body: data,
            });

            if (!response.success) {
                return rejectWithValue(response.error.message || "Failed to save pickup address.");
            }

            return response.data || {};
        } catch ({ error }) {
            return rejectWithValue(error.message || "An unexpected error occurred while saving the pickup address.");
        }
    }
);

export const removePickupAddress = createAsyncThunk(
    "pickupAddress/remove",
    async(id, { rejectWithValue }) => {
        try {
            const response = await request({
                endpoint: "/user/deletePickupAddress",
                method: "DELETE",
                params: { id },
            });

            if (!response.success) {
                return rejectWithValue(response.error.message || "Failed to remove pickup address.");
            }

            return id;
        } catch ({ error }) {
            return rejectWithValue(error.message || "An unexpected error occurred while removing the pickup address.");
        }
    }
);

export const updatePickupAddress = createAsyncThunk(
    "pickupAddress/update",
    async({ data, id }, { rejectWithValue }) => {
        try {
            const response = await request({
                endpoint: "/user/updatePickupAddress",
                method: "PUT",
                params: { addressId: id },
                body: data,
            });

            if (!response.success) {
                return rejectWithValue(response.error.message || "Failed to update pickup address.");
            }

            return response.data || {};
        } catch ({ error }) {
            return rejectWithValue(error.message || "An unexpected error occurred while updating the pickup address.");
        }
    }
);

// Initial state
const initialState = pickupAddressAdapter.getInitialState({
    loading: false,
    error: null,
});

// Slice
const pickupAddressSlice = createSlice({
    name: "pickupAddress",
    initialState,
    reducers: {
        addPickupAddresses: (state, action) => {
            pickupAddressAdapter.setAll(state, action.payload);
        },
        clearPickupAddresses: (state) => {
            pickupAddressAdapter.removeAll(state);
            state.loading = false;
            state.error = null;
        },

    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchPickupAddresses.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchPickupAddresses.fulfilled, (state, action) => {
                pickupAddressAdapter.setAll(state, action.payload);
                state.loading = false;
            })
            .addCase(fetchPickupAddresses.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(savePickupAddress.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(savePickupAddress.fulfilled, (state, action) => {
                pickupAddressAdapter.upsertOne(state, action.payload);
                state.loading = false;
            })
            .addCase(savePickupAddress.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(removePickupAddress.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(removePickupAddress.fulfilled, (state, action) => {
                pickupAddressAdapter.removeOne(state, action.payload);
                state.loading = false;
            })
            .addCase(removePickupAddress.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(updatePickupAddress.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updatePickupAddress.fulfilled, (state, action) => {
                pickupAddressAdapter.upsertOne(state, action.payload);
                state.loading = false;
            })
            .addCase(updatePickupAddress.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });
    },
});

// Selectors
export const {
    selectAll: selectAllPickupAddresses,
    selectById: selectPickupAddressById,
    selectIds: selectPickupAddressIds,
} = pickupAddressAdapter.getSelectors((state) => state.pickupAddress);

// Actions & Reducer Export
export const { clearPickupAddresses, addPickupAddresses, } = pickupAddressSlice.actions;
export default pickupAddressSlice.reducer;